import { useQueryClient } from '@tanstack/react-query';

import { PublicationSettingsQuery, usePublicationSettingUpdateMutation } from 'data/generated/graphql';

/**
 * Saves publication settings, then puts the saved settings in the PublicationSettings query's cache
 * (unless a read since brought a newer version), so the screen shows them and its next save carries
 * the version this one committed. The save's response selects the same fields as the query (the
 * PublicationSettingFields fragment).
 */
const useSavePublicationSetting = () => {
  const queryClient = useQueryClient();

  return usePublicationSettingUpdateMutation({
    // Hook-level, so it runs before mutateAsync resolves, and even if the screen has unmounted.
    onSuccess: async (data, { publicationSettingUpdateInput }) => {
      const queryKey = ['PublicationSettings', { publicationKey: publicationSettingUpdateInput.publicationKey }];
      // A read still in flight may have been answered before this save committed, and would put the
      // older settings back when it lands. Cancelling it also puts back the cache as it was when the
      // read started, so the saved settings are written after the cancel.
      await queryClient.cancelQueries({ queryKey, exact: true });
      // A read that already landed may be newer than this save: another save committed after it.
      const cachedVersion = queryClient.getQueryData<PublicationSettingsQuery>(queryKey)?.publicationSetting.version;
      const savedVersion = data.publicationSettingUpdate.version;
      if (cachedVersion != null && savedVersion != null && cachedVersion > savedVersion) return;
      queryClient.setQueryData<PublicationSettingsQuery>(queryKey, {
        publicationSetting: data.publicationSettingUpdate
      });
    }
  });
};

export default useSavePublicationSetting;
